import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtEur, INV_CATEGORIES, labelOf, VAT_RATES, grossOf } from "@/lib/format";
import { Plus, Trash2, Pencil, X, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ name: "", category: "audio", purchase_cost: 0, vat_rate: 22, quantity: 1, rental_price: 0, times_rented: 0, status: "disponibile", notes: "" });

const STATUS_STYLE = {
  disponibile: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  noleggiato: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  manutenzione: "bg-red-500/10 text-red-400 border-red-500/20",
};
const STATUS = [
  { value: "disponibile", label: "Disponibile" },
  { value: "noleggiato", label: "Noleggiato" },
  { value: "manutenzione", label: "Manutenzione" },
];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = () => api.listInventory().then((d) => { setItems(d); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditId(null); setModal(true); };
  const openEdit = (e) => { setForm({ ...empty(), ...e }); setEditId(e.id); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome");
    const payload = { ...form, purchase_cost: Number(form.purchase_cost) || 0, vat_rate: Number(form.vat_rate) || 0, quantity: Number(form.quantity) || 1, rental_price: Number(form.rental_price) || 0, times_rented: Number(form.times_rented) || 0 };
    try {
      if (editId) { await api.updateInventory(editId, payload); toast.success("Articolo aggiornato"); }
      else { await api.createInventory(payload); toast.success("Articolo aggiunto"); }
      setModal(false); load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const del = async (id) => { await api.deleteInventory(id); toast.success("Eliminato"); load(); };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-slate-100">Magazzino</h1>
          <p className="text-slate-500 text-sm mt-1">Attrezzatura audio, luci e video · ritorno sull'investimento</p>
        </div>
        <button data-testid="add-inventory-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 active:scale-[0.98]">
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </div>

      {items.length === 0 && (
        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-10 text-center">
          <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Magazzino vuoto. Aggiungi mixer, fari, casse, proiettori…</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
        {items.map((it) => {
          const earned = Number(it.rental_price || 0) * Number(it.times_rented || 0);
          const roi = it.purchase_cost ? (earned / it.purchase_cost) * 100 : 0;
          return (
            <div key={it.id} data-testid={`inventory-card-${it.id}`} className="bg-[#121824] border border-[#222D42] rounded-xl p-5 hover:border-[#384B6E] transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-head font-semibold text-slate-100 truncate">{it.name}</h3>
                  <p className="text-slate-500 text-xs mt-0.5">{labelOf(INV_CATEGORIES, it.category)} · Qtà {it.quantity}</p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLE[it.status]}`}>{labelOf(STATUS, it.status)}</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="bg-[#1A2335] rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500">Acquisto{it.vat_rate > 0 ? ` +IVA ${it.vat_rate}%` : ""}</p><p className="font-mono text-sm text-slate-200">{fmtEur(grossOf(it.purchase_cost, it.vat_rate))}</p></div>
                <div className="bg-[#1A2335] rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500">Noleggio/evento</p><p className="font-mono text-sm text-slate-200">{fmtEur(it.rental_price)}</p></div>
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs"><span className="text-slate-500">Noleggiato {it.times_rented}x · Incassato</span><span className="font-mono text-slate-300">{fmtEur(earned)}</span></div>
                <div className="w-full h-1.5 bg-[#1A2335] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${roi >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(roi, 100)}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs"><span className="text-slate-500">ROI ammortamento</span><span className={`font-mono font-semibold ${roi >= 100 ? "text-emerald-400" : "text-amber-400"}`}>{roi.toFixed(0)}%</span></div>
              </div>
              <div className="mt-3 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`edit-inventory-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-md hover:bg-[#222D42] text-slate-400"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-inventory-${it.id}`} onClick={() => del(it.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModal(false)} />
          <div className="relative bg-[#121824] border border-[#222D42] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#222D42]">
              <h3 className="font-head text-lg font-semibold text-slate-100">{editId ? "Modifica Articolo" : "Nuovo Articolo"}</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="lbl">Nome</label><input data-testid="inventory-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="lbl">Categoria</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp">{INV_CATEGORIES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                <div><label className="lbl">Stato</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="inp">{STATUS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="lbl">Costo netto (€)</label><input type="number" data-testid="inventory-cost-input" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} className="inp font-mono" /></div>
                <div><label className="lbl">IVA</label><select data-testid="inventory-vat-select" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })} className="inp">{VAT_RATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                <div><label className="lbl">Quantità</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="inp font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="lbl">Noleggio/evento (€)</label><input type="number" value={form.rental_price} onChange={(e) => setForm({ ...form, rental_price: e.target.value })} className="inp font-mono" /></div>
                <div><label className="lbl">Volte noleggiato</label><input type="number" value={form.times_rented} onChange={(e) => setForm({ ...form, times_rented: e.target.value })} className="inp font-mono" /></div>
              </div>
            </div>
            <div className="p-5 border-t border-[#222D42] flex justify-end gap-2">
              <button onClick={() => setModal(false)} className="bg-[#1A2335] hover:bg-[#222D42] text-slate-200 border border-[#222D42] font-medium px-4 py-2 rounded-lg transition-colors">Annulla</button>
              <button data-testid="save-inventory-button" onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors">Salva</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}.lbl{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;color:#64748B;font-weight:500;display:block;margin-bottom:.375rem}`}</style>
    </div>
  );
}
